import { createFileRoute, Link } from "@tanstack/react-router";

import { LodgeApiError } from "../api/client";
import type {
  Person,
  PersonAffiliation,
  PersonProductUser,
  PersonProductUserGrant,
} from "../api/hooks";
import {
  relationshipLabel,
  roleLabel,
  sourceLabel,
  tagLabel,
  useLodgeUsers,
  usePerson,
} from "../api/hooks";
import { dateLabel, monthLabel } from "../lib/relativeTime";

import styles from "./people_.$personId.module.scss";

// Two letters from the name, for the avatar. Shared shape with the index's,
// which draws the same person smaller.
//
// Spread rather than [0]: a name starting outside the BMP indexes to half a
// surrogate pair and draws as a replacement glyph.
function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter((part) => part !== "");

  return parts
    .slice(0, 2)
    .map((part) => [...part][0]?.toUpperCase() ?? "")
    .join("");
}

// Every role somebody holds, gathered from across their organisations and shown
// once beside their name. Design 8b puts them on the person for the same reason
// 8a does on the index row: what an operator scans for is "is this the person
// who decides", which is true of them however many organisations they sit at.
function rolesHeld(person: Person): string[] {
  const held = new Set<string>();
  for (const affiliation of person.organisations) {
    for (const role of affiliation.roles) held.add(role);
  }

  return [...held];
}

// The regions somebody works in, from the organisations they belong to. Design
// 8b draws one chip; somebody working across two counties gets both rather than
// whichever happened to sort first.
function regions(person: Person): string[] {
  const seen = new Set<string>();
  for (const affiliation of person.organisations) {
    if (affiliation.organisationCounty !== "") seen.add(affiliation.organisationCounty);
  }

  return [...seen];
}

// The line under the name: "Estate Director at Comptonbury Estate".
//
// The organisation is the first one they belong to, which is the order the API
// sorts them in - roles first, then by name - so it is the one the record is
// most about rather than an arbitrary pick. Somebody at two is named by one
// here and by both in the panel below.
function PrimaryLine({ person }: { person: Person }) {
  const first = person.organisations[0];

  if (person.jobTitle === "" && first === undefined) return null;

  return (
    <div className={styles.primaryLine}>
      {person.jobTitle !== "" && <span>{person.jobTitle}</span>}
      {person.jobTitle !== "" && first !== undefined && <span> at </span>}
      {/* Not a link. Design 8b points this at the organisation record, which
          does not exist yet - ad93acd1-0043-4011-9742-e56febdbc00e - and a link
          to nowhere is worse than a name. */}
      {first !== undefined && (
        <span className={styles.primaryOrganisation}>
          {organisationLabel(first.organisationName)}
        </span>
      )}
    </div>
  );
}

// The chip row beside the name: what they are to us, at a glance, before
// reading anything below.
function Chips({ person }: { person: Person }) {
  const roles = rolesHeld(person);
  const count = person.organisations.length;

  return (
    <div className={styles.chips}>
      {roles.map((role) => (
        <span key={role} className={styles.roleChip}>
          {roleLabel(role)}
        </span>
      ))}

      {count > 0 && (
        <span className={styles.chip}>
          {count === 1 ? "1 organisation" : `${count} organisations`}
        </span>
      )}

      {/* What they are in the app, when their address matches an account. */}
      {person.productUser !== null && person.productUser.grants.length > 0 && (
        <span className={styles.chip}>{appRoles(person.productUser).join(", ")} in the app</span>
      )}

      {regions(person).map((region) => (
        <span key={region} className={styles.chip}>
          {region}
        </span>
      ))}
    </div>
  );
}

// One organisation card. The relationship chip is the thing worth seeing first:
// whether the person you are about to email belongs to a customer or to a body
// nobody has approached.
function Affiliation({ affiliation }: { affiliation: PersonAffiliation }) {
  // "Estate Director - decision maker and billing - since March 2025", built
  // from whichever of the three parts we actually hold. Joined rather than
  // written out, because most affiliations have one of them and a template with
  // empty gaps reads as missing data.
  const parts: string[] = [];

  if (affiliation.roles.length > 0) {
    parts.push(
      affiliation.roles.map((role) => roleLabel(role).toLowerCase()).join(" and "),
    );
  }

  if (affiliation.started !== null) {
    parts.push(`since ${monthLabel(affiliation.started)}`);
  }

  return (
    <div className={styles.affiliation}>
      <div className={styles.affiliationHead}>
        <span className={styles.affiliationName}>
          {organisationLabel(affiliation.organisationName)}
        </span>
        {/* An empty relationship is one the API could not derive, which is not
            the same as "not engaged". Drawing the latter would tell an operator
            nobody has spoken to a customer. */}
        {affiliation.relationship !== "" && (
          <span className={`${styles.relationship} ${relationshipClass(affiliation)}`}>
            {relationshipLabel(affiliation.relationship)}
          </span>
        )}
      </div>

      {parts.length > 0 && <div className={styles.affiliationDetail}>{parts.join(" · ")}</div>}

      {affiliation.notes !== "" && (
        <div className={styles.affiliationNotes}>{affiliation.notes}</div>
      )}
    </div>
  );
}

function relationshipClass(affiliation: PersonAffiliation): string {
  switch (affiliation.relationship) {
    case "customer":
      return styles.relationshipCustomer;
    case "in_pipeline":
      return styles.relationshipPipeline;
    case "lapsed":
      return styles.relationshipLapsed;
    default:
      return styles.relationshipQuiet;
  }
}

// A fact and its value, for the rail panels. Missing values are left out
// entirely rather than drawn as "Not recorded": the rail is a summary, and a
// column of blanks is what a record with three facts in it looks like.
function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div className={styles.fact}>
      <span className={styles.factLabel}>{label}</span>
      <span className={styles.factValue}>{value}</span>
    </div>
  );
}

// What one grant reaches: the organisation it was made across, or the sites it
// was made on. A grant with neither is drawn as the bare role rather than as a
// count of nothing.
// An organisation's name is not null but may be blank, the same way a Lodge
// user's may be. Named in words so a line reads "across an unnamed
// organisation" rather than trailing off after its preposition.
function organisationLabel(name: string): string {
  return name.trim() === "" ? "an unnamed organisation" : name;
}

function grantLabel(grant: PersonProductUserGrant): string {
  const role = roleLabel(grant.roleName);

  if (grant.organisationId !== null) {
    return `${role} across ${organisationLabel(grant.organisationName)}`;
  }

  if (grant.siteCount === 0) {
    return role;
  }

  return `${role} on ${grant.siteCount} ${grant.siteCount === 1 ? "site" : "sites"}`;
}


// The roles somebody holds in the app, each named once however many grants
// carry it.
function appRoles(account: PersonProductUser): string[] {
  return [...new Set(account.grants.map((grant) => roleLabel(grant.roleName)))];
}

function AccountPanel({ account }: { account: PersonProductUser }) {
  return (
    <div className="panel">
      <div className={styles.panelTitle}>Ecosystem Tracker account</div>
      <div className={styles.facts}>
        <div className={styles.fact}>
          <span className={styles.factLabel}>Role</span>
          <span className={styles.factValue}>
            {account.grants.length === 0 ? (
              // An account nobody has granted anything to. Said in words rather
              // than left out, because a panel with only a date on it reads as
              // roles that failed to load.
              <span className={styles.unknown}>No roles yet</span>
            ) : (
              account.grants.map((grant) => (
                <span
                  key={`${grant.roleId}:${grant.organisationId ?? ""}`}
                  className={styles.grant}
                >
                  {grantLabel(grant)}
                  {/* Whose role it is. Two organisations can each define an
                      "Ecologist" and a person can hold both, which without this
                      reads as the same line twice. Keyed on the id, so a role
                      owned by a blank-named organisation still draws as
                      something rather than silently merging the two lines. */}
                  {grant.roleOrganisationId !== null && (
                    <span className={styles.grantOwner}>
                      {organisationLabel(grant.roleOrganisationName)}
                    </span>
                  )}
                </span>
              ))
            )}
          </span>
        </div>
        {/* The design says "Invited". Nothing records when an invitation was
            sent, so this says what the value is instead of what we wish it
            were - c7e290d4-3026-4cd5-a631-60218394d74e covers the sign-in
            history the panel's fourth line wants. */}
        <Fact label="Account made" value={dateLabel(account.created)} />
      </div>
      <Link to="/users" className={styles.panelLink}>
        Open user in Users
      </Link>
    </div>
  );
}

function Record({ person, ownerName }: { person: Person; ownerName: string }) {
  return (
    <>
      <nav className={styles.breadcrumb} aria-label="Breadcrumb">
        <Link to="/people">People</Link>
        <span aria-hidden="true">›</span>
        <span aria-current="page">{person.name}</span>
      </nav>

      <div className={styles.header}>
        <div className={styles.identity}>
          <span className={styles.avatar} aria-hidden="true">
            {initials(person.name)}
          </span>
          <div className={styles.identityText}>
            <div className={styles.nameRow}>
              <h1 className={styles.name}>{person.name}</h1>
              <PrimaryLine person={person} />
            </div>
            <Chips person={person} />
          </div>
        </div>
      </div>

      <div className={styles.columns}>
        <div className={styles.mainColumn}>
          <div className="panel">
            <div className={styles.panelHead}>
              <div className={styles.panelTitle}>Organisations</div>
            </div>

            {person.organisations.length === 0 ? (
              // The Unlinked state, which is a job rather than an absence: this
              // is somebody who arrived from a personal address and was never
              // joined up to the body they came from.
              <div className={styles.state}>Not linked to an organisation yet.</div>
            ) : (
              <div className={styles.affiliations}>
                {person.organisations.map((affiliation) => (
                  <Affiliation key={affiliation.id} affiliation={affiliation} />
                ))}
              </div>
            )}
          </div>
        </div>

        <div className={styles.rail}>
          <div className="panel">
            <div className={styles.panelTitle}>Contact</div>
            <div className={styles.contact}>
              {person.email !== "" ? (
                <a className={styles.contactLine} href={`mailto:${person.email}`}>
                  {person.email}
                </a>
              ) : (
                <span className={styles.unknown}>No email address</span>
              )}
              {person.phone !== "" && (
                <span className={styles.contactLine}>{person.phone}</span>
              )}
            </div>

            {person.notes !== "" && <div className={styles.notes}>{person.notes}</div>}
          </div>

          {person.productUser !== null && <AccountPanel account={person.productUser} />}

          <div className="panel">
            <div className={styles.panelTitle}>Facts</div>
            <div className={styles.facts}>
              <Fact label="Added" value={dateLabel(person.created)} />
              {person.source !== "" && (
                <Fact label="Source" value={sourceLabel(person.source)} />
              )}
              <Fact label="Owner" value={ownerName} />
            </div>

            {person.tags.length > 0 && (
              <div className={styles.tags}>
                {person.tags.map((tag) => (
                  <span key={tag} className={styles.tag}>
                    {tagLabel(tag)}
                  </span>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

function PersonRecord() {
  const { personId } = Route.useParams();
  const { data: person, isPending, isError, error } = usePerson(personId);

  // Owners are ids on a person; the picker's endpoint is what turns them into
  // names. Same three states the index keeps apart, for the same reason:
  // "Unassigned" said about a record that does have an owner invites somebody
  // to take over a colleague's work.
  const { data: lodgeUsers } = useLodgeUsers();
  const ownerName = ownerNameFor(person, lodgeUsers ?? []);

  // A record that is gone and a read that failed are different things to be
  // told: one is somebody deleting the person while this tab was open, and the
  // other is worth retrying.
  const isMissing = error instanceof LodgeApiError && error.status === 404;

  return (
    <>
      {isPending && (
        <div className="panel">
          <div className={styles.state} role="status">
            Loading…
          </div>
        </div>
      )}

      {isError && (
        <div className="panel">
          <div className={styles.state} role="alert">
            {isMissing
              ? "That person is no longer here. They may have been deleted."
              : "This person could not be loaded."}
          </div>
        </div>
      )}

      {person !== undefined && <Record person={person} ownerName={ownerName} />}
    </>
  );
}

function ownerNameFor(
  person: Person | undefined,
  lodgeUsers: { id: string; name: string }[],
): string {
  if (person === undefined || person.ownerLodgeUserId === null) return "Unassigned";

  // A blank name is as unusable to a reader as a missing one, and it is
  // reachable: lodge_users.name is NOT NULL but has no non-empty constraint.
  const owner = lodgeUsers.find((user) => user.id === person.ownerLodgeUserId);
  if (owner === undefined || owner.name.trim() === "") return "Owner unknown";

  return owner.name;
}

export const Route = createFileRoute("/people_/$personId")({
  component: PersonRecord,
});
